import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send cookies with requests
});

// Note: Auth is handled via httpOnly cookies (withCredentials: true)
// No need to manually add Authorization header - cookies are sent automatically

// Handle 401 errors - but NOT for auth probe endpoints
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for /auth/me - it's just a soft auth check
      const isAuthProbe = error.config?.url?.includes('/auth/me');
      if (!isAuthProbe) {
        // Clear any localStorage remnants (user UI state only, not tokens)
        localStorage.removeItem('user');
        // Only redirect if not already on login/register page
        if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
};

// Products API
export const productsAPI = {
  getCoordinated: (params = {}) => api.get('/products/sha-coordinated', { params }).then(res => ({
    ...res,
    data: res.data.products || [],
    coordination: res.data._meta || {},
    total_count: res.data._meta?.total_active_catalog || res.data.products?.length || 0,
  })),
  getAll: (params) => api.get('/products', { params }).then(res => {
    // Handle new response format with metadata
    if (res.data.products) {
      return { 
        ...res, 
        data: res.data.products,
        total_count: res.data._meta?.total_count || res.data.products.length
      };
    }
    return { ...res, total_count: res.data?.length || 0 };
  }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/admin/products', data),
  update: (id, data) => api.put(`/admin/products/${id}`, data),
  delete: (id) => api.delete(`/admin/products/${id}`),
};

// Cart API
export const cartAPI = {
  get: () => api.get('/cart'),
  add: (productId, quantity = 1, size = null, color = null, customPhotoUrl = null) => api.post('/cart', { product_id: productId, quantity, size, color, custom_photo_url: customPhotoUrl }),
  update: (itemId, quantity) => api.put(`/cart/${itemId}?quantity=${quantity}`),
  remove: (itemId) => api.delete(`/cart/${itemId}`),
  clear: () => api.delete('/cart'),
};

// Orders API
export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  getAll: () => api.get('/orders'),
  getById: (id) => api.get(`/orders/${id}`),
  // Admin
  getAllAdmin: (params) => api.get('/admin/orders', { params }),
  updateStatus: (id, status) => api.put(`/admin/orders/${id}/status?status=${status}`),
  updateSupplierStatus: (orderId, supplierId, status) => 
    api.put(`/admin/orders/${orderId}/supplier/${supplierId}/status?status=${status}`),
};

// Suppliers API
export const suppliersAPI = {
  getAll: () => api.get('/admin/suppliers'),
  create: (data) => api.post('/admin/suppliers', data),
  update: (id, data) => api.put(`/admin/suppliers/${id}`, data),
  delete: (id) => api.delete(`/admin/suppliers/${id}`),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getInventory: (params) => api.get('/admin/inventory', { params }),
  updateStock: (productId, stock) => api.put(`/admin/inventory/${productId}/stock?stock=${stock}`),
  seedData: () => api.post('/admin/seed'),
};

// Categories API
export const categoriesAPI = {
  getAll: () => api.get('/categories').then(res => {
    // Handle new response format with metadata
    if (res.data.categories) {
      return { ...res, data: res.data.categories };
    }
    return res;
  }),
};

// Marketing OAuth API - uses window.location.origin for cross-environment compatibility
export const marketingOAuthAPI = {
  // Start Reddit OAuth
  startRedditAuth: () => {
    const frontendOrigin = window.location.origin;
    return api.get(`/marketing/reddit/auth?frontend_origin=${encodeURIComponent(frontendOrigin)}`);
  },
  // Start Google OAuth
  startGoogleAuth: () => {
    const frontendOrigin = window.location.origin;
    return api.get(`/marketing/google/auth?frontend_origin=${encodeURIComponent(frontendOrigin)}`);
  },
  // Get Reddit status
  getRedditStatus: () => api.get('/marketing/reddit/status'),
  // Get Google status
  getGoogleStatus: () => api.get('/marketing/google/status'),
};

// Integrations API - uses window.location.origin for webhooks
export const integrationsAPI = {
  getWebhookUrls: () => {
    const frontendOrigin = window.location.origin;
    return api.get(`/integrations/webhooks/urls?frontend_origin=${encodeURIComponent(frontendOrigin)}`);
  },
};

export default api;
